import { Input } from "@/components/ui/input";
import { useDomains } from "@/hooks";
import { cn } from "@/lib/utils";
import { INTENT_LIMITS, type IntentHorizon, type OrgIntent } from "@/types/api";
import { X } from "lucide-react";
import { useState } from "react";

const HORIZONS: { value: IntentHorizon; label: string }[] = [
	{ value: "month", label: "This month" },
	{ value: "quarter", label: "This quarter" },
	{ value: "half", label: "This half" },
	{ value: "year", label: "This year" },
	{ value: "ongoing", label: "Ongoing" },
];

/**
 * Editing what a team — or the whole organization — is trying to get better at.
 *
 * Shared by both so the rules are written once. The two differ only in scope,
 * and having a second copy of the caps and the domain picker is how they end up
 * disagreeing about what a valid intent is.
 *
 * NOTHING READS THIS YET, and the screens using it say so. Implying that saving
 * a goal changes tomorrow's feed would be a promise the backend does not
 * currently keep (CUR-126), and a partner discovering that themselves is worse
 * than not offering the field.
 *
 * The caps mirror the Gateway's (utils/orgIntent.ts). They are here to warn
 * BEFORE a round trip, never to decide: the server validates independently and
 * will still refuse, and normalises what it accepts — so what comes back may
 * not be character-for-character what was typed.
 */
export function IntentEditor({
	value,
	onChange,
	disabled,
}: {
	value: OrgIntent;
	onChange: (next: OrgIntent) => void;
	disabled?: boolean;
}) {
	const { data: catalogue } = useDomains();
	const [focusDraft, setFocusDraft] = useState("");

	const domains = value.domains ?? [];
	const focus = value.focus ?? [];
	const goal = value.goal ?? "";

	const set = (patch: Partial<OrgIntent>) => onChange({ ...value, ...patch });

	const toggleDomain = (label: string) => {
		const next = domains.includes(label)
			? domains.filter((d) => d !== label)
			: [...domains, label];
		// Silently dropping the extra would look like the click did nothing.
		if (next.length > INTENT_LIMITS.domainsMax) return;
		set({ domains: next });
	};

	const addFocus = () => {
		const entry = focusDraft.trim();
		if (!entry) return;
		// Case-insensitive, matching the server: "ASC 606" and "asc 606" are one
		// concept, and it would reject the pair anyway.
		if (focus.some((f) => f.toLowerCase() === entry.toLowerCase())) {
			setFocusDraft("");
			return;
		}
		if (focus.length >= INTENT_LIMITS.focusMax) return;
		if (entry.length > INTENT_LIMITS.entryMax) return;
		set({ focus: [...focus, entry] });
		setFocusDraft("");
	};

	return (
		<div className="flex flex-col gap-5">
			<Field
				label="What are they working towards?"
				hint={`${goal.length}/${INTENT_LIMITS.goalMax}`}
				hintTone={goal.length > INTENT_LIMITS.goalMax ? "error" : "muted"}
			>
				<Input
					value={goal}
					disabled={disabled}
					placeholder="Close the books faster and get audit-ready"
					onChange={(e) => set({ goal: e.target.value })}
				/>
			</Field>

			<Field
				label="Subjects"
				hint={`${domains.length}/${INTENT_LIMITS.domainsMax}`}
			>
				{catalogue ? (
					<div className="flex flex-wrap gap-2">
						{catalogue.domains.map((label) => {
							const on = domains.includes(label);
							return (
								<button
									key={label}
									type="button"
									disabled={disabled}
									onClick={() => toggleDomain(label)}
									className={cn(
										"rounded-full border px-3 py-1 text-caption transition-colors",
										on
											? "border-transparent bg-surface3 font-medium text-heading"
											: "border-divider text-muted-foreground hover:text-heading",
										disabled && "opacity-50",
									)}
								>
									{label}
								</button>
							);
						})}
					</div>
				) : (
					// A picker, not free text, precisely because a label typed by
					// hand ("Accounting & FPA") is rejected by the server for a
					// reason nobody can see.
					<p className="text-caption text-muted-foreground">
						Loading subjects…
					</p>
				)}
			</Field>

			<Field
				label="Specific topics"
				hint={`${focus.length}/${INTENT_LIMITS.focusMax}`}
			>
				<p className="text-caption text-muted-foreground">
					These topics are what match learning content; the sentence above is
					the headline.
				</p>
				<div className="flex flex-wrap gap-2">
					{focus.map((entry) => (
						<span
							key={entry}
							className="flex items-center gap-1 rounded-full bg-surface3 px-3 py-1 text-caption text-heading"
						>
							{entry}
							<button
								type="button"
								disabled={disabled}
								aria-label={`Remove ${entry}`}
								onClick={() => set({ focus: focus.filter((f) => f !== entry) })}
								className="text-muted-foreground hover:text-heading"
							>
								<X className="h-3 w-3" />
							</button>
						</span>
					))}
				</div>
				<Input
					value={focusDraft}
					disabled={disabled || focus.length >= INTENT_LIMITS.focusMax}
					placeholder={
						focus.length >= INTENT_LIMITS.focusMax
							? "That's the maximum"
							: "ASC 606, then Enter"
					}
					maxLength={INTENT_LIMITS.entryMax}
					onChange={(e) => setFocusDraft(e.target.value)}
					onKeyDown={(e) => {
						if (e.key !== "Enter") return;
						// Otherwise this submits the dialog it sits inside.
						e.preventDefault();
						addFocus();
					}}
					onBlur={addFocus}
				/>
			</Field>

			<Field label="Over what period?">
				<div className="flex flex-wrap gap-2">
					{HORIZONS.map((h) => (
						<button
							key={h.value}
							type="button"
							disabled={disabled}
							onClick={() =>
								set({
									horizon: value.horizon === h.value ? undefined : h.value,
								})
							}
							className={cn(
								"rounded-full border px-3 py-1 text-caption transition-colors",
								value.horizon === h.value
									? "border-transparent bg-surface3 font-medium text-heading"
									: "border-divider text-muted-foreground hover:text-heading",
								disabled && "opacity-50",
							)}
						>
							{h.label}
						</button>
					))}
				</div>
			</Field>
		</div>
	);
}

function Field({
	label,
	hint,
	hintTone = "muted",
	children,
}: {
	label: string;
	hint?: string;
	hintTone?: "muted" | "error";
	children: React.ReactNode;
}) {
	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-baseline justify-between gap-3">
				<span className="text-caption font-medium text-heading">{label}</span>
				{hint ? (
					<span
						className={cn(
							"text-caption tabular-nums",
							hintTone === "error" ? "text-error" : "text-muted-foreground",
						)}
					>
						{hint}
					</span>
				) : null}
			</div>
			{children}
		</div>
	);
}
